import { errorCodeOf, errorDetailsOf } from "@luckymenu/api-client";
import { useMutation, useQuery, useQueryClient, useTRPC } from "@luckymenu/api-client/react";
import { type OfferStatus, offerStatus } from "@luckymenu/billing-core/offers";
import {
	addDays,
	DEFAULT_TIME_ZONE,
	type Offer,
	type OfferList,
	restaurantBusinessDate,
} from "@luckymenu/core";
import { useCallback, useMemo, useState } from "react";
import {
	type DescribableOffer,
	newOfferDraft,
	OFFER_STEPS,
	type OfferDraft,
	type OfferErrors,
	type OfferNames,
	type OfferStep,
	offerDraftToInput,
	offerStepErrors,
	offerToDraft,
	setOfferKind,
	stepOfField,
	toggleOfferDay,
	toggleOfferTarget,
} from "../offers/form";
import { useFileUpload } from "./use-file-upload";
import { useNow } from "./use-live-board";
import { useMenu } from "./use-menu";
import { useCan } from "./use-navigation";
import { useStoredDraft } from "./use-stored-draft";

/**
 * More > Offers (MG-05, P9-T08, P9-T09) for both staff apps. See
 * docs/architecture/api.md, Promotions. Needs `offers.manage`.
 */

/** Why an offer change was refused. Both apps translate `offers:problems.<problem>`. */
export type OfferProblem =
	| "network"
	| "forbidden"
	| "notInPlan"
	| "notFound"
	| "taken"
	| "photo"
	| "failed";

export function offerProblemOf(error: unknown): OfferProblem {
	const code = errorCodeOf(error);
	const details = errorDetailsOf(error);
	if (code === "NETWORK") return "network";
	if (code === "FORBIDDEN") return details.reason === "not_in_plan" ? "notInPlan" : "forbidden";
	if (code === "NOT_FOUND") return "notFound";
	if (code === "CONFLICT" && details.reason === "taken") return "taken";
	if (code === "VALIDATION" && details.field === "fileId") return "photo";
	return "failed";
}

/** Names of dishes and categories from the cached menu, for the plain language preview. */
export function useOfferNames(): OfferNames {
	const menu = useMenu();
	return useMemo(() => {
		const items = new Map((menu.data?.items ?? []).map((i) => [i.id, i.name]));
		const categories = new Map((menu.data?.categories ?? []).map((c) => [c.id, c.name]));
		return { item: (id) => items.get(id), category: (id) => categories.get(id) };
	}, [menu.data]);
}

export type OfferRow = Offer & { status: OfferStatus };

/**
 * The list with its switches: each offer with its status now (active,
 * scheduled, ended, off), refreshed every half minute. `toggle` and
 * `remove` update the list at once and roll back when refused.
 */
export function useOffers() {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const allowed = useCan("offers.manage");
	const query = useQuery({ ...trpc.staff.promotions.list.queryOptions(), enabled: allowed });
	const now = useNow();
	const [problem, setProblem] = useState<OfferProblem | null>(null);
	const key = trpc.staff.promotions.list.queryKey();
	const setActive = useMutation(trpc.staff.promotions.setActive.mutationOptions());
	const remove = useMutation(trpc.staff.promotions.delete.mutationOptions());
	const timeZone = query.data?.timeZone ?? DEFAULT_TIME_ZONE;

	const rows: OfferRow[] = useMemo(
		() =>
			(query.data?.offers ?? []).map((o) => ({
				...o,
				status: offerStatus(o, new Date(now), timeZone),
			})),
		[query.data, now, timeZone],
	);

	const patch = useCallback(
		(change: (list: OfferList) => OfferList) => {
			const before = queryClient.getQueryData<OfferList>(key);
			if (before) queryClient.setQueryData(key, change(before));
			return before;
		},
		[queryClient, key],
	);

	return {
		status: !allowed
			? ("forbidden" as const)
			: query.data
				? ("ready" as const)
				: query.isError
					? ("error" as const)
					: ("loading" as const),
		offers: rows,
		timeZone,
		retry: () => void query.refetch(),
		problem,
		clearProblem: () => setProblem(null),
		/** The on and off switch. */
		toggle: async (id: string, isActive: boolean) => {
			setProblem(null);
			const before = patch((l) => ({
				...l,
				offers: l.offers.map((o) => (o.id === id ? { ...o, isActive } : o)),
			}));
			try {
				const offer = await setActive.mutateAsync({ id, isActive });
				patch((l) => ({ ...l, offers: l.offers.map((o) => (o.id === id ? offer : o)) }));
			} catch (error) {
				if (before) queryClient.setQueryData(key, before);
				setProblem(offerProblemOf(error));
			}
		},
		remove: async (id: string): Promise<boolean> => {
			setProblem(null);
			try {
				await remove.mutateAsync({ id });
				patch((l) => ({ ...l, offers: l.offers.filter((o) => o.id !== id) }));
				return true;
			} catch (error) {
				setProblem(offerProblemOf(error));
				return false;
			}
		},
	};
}

/**
 * The Offers report (P9-T20) for the last `days` business dates: uses and
 * discount per offer, and the share of items added from suggestions. Needs
 * `reports.view` and a plan with reports; `status` says `unavailable`
 * otherwise.
 */
export function useOffersReport(timeZone: string, days = 30) {
	const trpc = useTRPC();
	const allowed = useCan("reports.view");
	const range = useMemo(() => {
		const to = restaurantBusinessDate({ timezone: timeZone }, new Date());
		return { from: addDays(to, -(days - 1)), to };
	}, [days, timeZone]);
	const query = useQuery({
		...trpc.staff.reports.offers.queryOptions(range),
		enabled: allowed,
		retry: false,
	});
	const report = query.data ?? null;
	const total = report?.items.total ?? 0;
	return {
		status:
			!allowed || query.isError
				? ("unavailable" as const)
				: report
					? ("ready" as const)
					: ("loading" as const),
		range,
		report,
		/** Whole percent of units added from "Goes well with", or null with none sold. */
		suggestionShare:
			total > 0 ? Math.round(((report?.items.fromSuggestions ?? 0) * 100) / total) : null,
	};
}

/**
 * The offer form in three steps (P9-T09): `offerId` to edit, or null for a
 * new offer. The draft is kept on the device until saved. `next()` checks
 * the step before moving on; `submit()` checks everything and goes back to
 * the first step with a problem. The banner uploads as `offer_banner`.
 */
export function useOfferForm({ offerId }: { offerId: string | null }) {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const list = useQuery(trpc.staff.promotions.list.queryOptions());
	const banner = useFileUpload("offer_banner");
	const names = useOfferNames();
	const [step, setStep] = useState<OfferStep>("kind");
	const [errors, setErrors] = useState<OfferErrors>({});
	const [problem, setProblem] = useState<OfferProblem | null>(null);
	const timeZone = list.data?.timeZone ?? DEFAULT_TIME_ZONE;

	const original = useMemo(
		() => (offerId ? (list.data?.offers.find((o) => o.id === offerId) ?? null) : null),
		[list.data, offerId],
	);
	const loaded = list.data !== undefined;
	const initial = useMemo(() => {
		if (offerId) return original ? offerToDraft(original, timeZone) : null;
		return loaded ? newOfferDraft() : null;
	}, [offerId, original, loaded, timeZone]);
	const stored = useStoredDraft(
		offerId ? `offer:${offerId}` : "offer:new",
		initial,
		offerId
			? (d: OfferDraft) => JSON.stringify(d)
			: (d: OfferDraft) => JSON.stringify({ ...d, id: "" }),
	);
	const draft = stored.draft;
	const save = useMutation(trpc.staff.promotions.upsert.mutationOptions());

	const edit = useCallback(
		(change: (d: OfferDraft) => OfferDraft) => {
			setProblem(null);
			stored.update(change);
		},
		[stored.update],
	);
	const clear = (...keys: string[]) =>
		setErrors((e) => {
			if (!keys.some((k) => k in e)) return e;
			const next = { ...e };
			for (const k of keys) delete next[k];
			return next;
		});

	/** What the form would save, for the plain language preview; null while incomplete. */
	const preview = useMemo((): DescribableOffer | null => {
		if (!draft) return null;
		const result = offerDraftToInput(draft, timeZone);
		if (!result.ok) return null;
		const i = result.input;
		return {
			kind: i.kind,
			discountType: i.discountType,
			value: i.value,
			minOrderPaise: i.minOrderPaise ?? null,
			maxDiscountPaise: i.maxDiscountPaise ?? null,
			code: i.code ?? null,
			appliesTo: i.appliesTo ?? "all",
			itemIds: i.itemIds ?? [],
			categoryIds: i.categoryIds ?? [],
			startsAt: i.startsAt ?? null,
			endsAt: i.endsAt ?? null,
			daysOfWeek: i.daysOfWeek ?? [],
			timeFrom: i.timeFrom ?? null,
			timeTo: i.timeTo ?? null,
		};
	}, [draft, timeZone]);

	return {
		status: list.isPending
			? ("loading" as const)
			: offerId && loaded && !original
				? ("notFound" as const)
				: !draft
					? ("loading" as const)
					: ("ready" as const),
		isNew: offerId === null,
		original,
		draft,
		restored: stored.restored,
		dirty: stored.dirty,
		timeZone,
		names,
		/** The three steps, and where the form is. */
		steps: OFFER_STEPS,
		step,
		goTo: setStep,
		/** Checks this step; moves on when it is right. */
		next: () => {
			if (!draft) return;
			const found = offerStepErrors(draft, step, timeZone);
			// The name is asked on the first step but may wait for the last.
			delete found.name;
			setErrors(found);
			if (Object.keys(found).length > 0) return;
			const at = OFFER_STEPS.indexOf(step);
			const following = OFFER_STEPS[at + 1];
			if (following) setStep(following);
		},
		back: () => {
			const at = OFFER_STEPS.indexOf(step);
			const before = OFFER_STEPS[at - 1];
			if (before) setStep(before);
		},
		errors,
		problem,
		saving: save.isPending,
		preview,

		set: <K extends keyof OfferDraft>(field: K, value: OfferDraft[K]) => {
			clear(field as string, ...(field === "timed" || field === "timeFrom" ? ["timeTo"] : []));
			edit((d) => ({ ...d, [field]: value }));
		},
		setKind: (kind: OfferDraft["kind"]) => {
			clear("discountType", "code", "minOrder");
			edit((d) => setOfferKind(d, kind));
		},
		toggleDay: (day: number) => edit((d) => toggleOfferDay(d, day)),
		toggleTarget: (kind: "items" | "categories", id: string) => {
			clear(kind === "items" ? "itemIds" : "categoryIds");
			edit((d) => toggleOfferTarget(d, kind, id));
		},
		banner: banner.state,
		pickBanner: async () => {
			const info = await banner.pickAndUpload();
			if (info) edit((d) => ({ ...d, banner: { id: info.id, blurhash: info.blurhash } }));
		},
		removeBanner: () => {
			banner.reset();
			edit((d) => ({ ...d, banner: null }));
		},
		discard: () => {
			setErrors({});
			setProblem(null);
			setStep("kind");
			banner.reset();
			stored.reset();
		},

		submit: async (): Promise<Offer | null> => {
			if (!draft) return null;
			const result = offerDraftToInput(draft, timeZone);
			if (!result.ok) {
				setErrors(result.errors);
				const first = Object.keys(result.errors)[0];
				if (first) setStep(stepOfField(first));
				return null;
			}
			setErrors({});
			setProblem(null);
			try {
				const offer = await save.mutateAsync(result.input);
				const key = trpc.staff.promotions.list.queryKey();
				queryClient.setQueryData<OfferList>(key, (l) =>
					l
						? {
								...l,
								offers: l.offers.some((o) => o.id === offer.id)
									? l.offers.map((o) => (o.id === offer.id ? offer : o))
									: [...l.offers, offer],
							}
						: l,
				);
				if (offerId) stored.reset(offerToDraft(offer, timeZone));
				else stored.forget();
				return offer;
			} catch (error) {
				const found = offerProblemOf(error);
				setProblem(found);
				if (found === "taken") {
					setErrors({ code: "taken" });
					setStep("kind");
				}
				return null;
			}
		},
	};
}

export type OfferForm = ReturnType<typeof useOfferForm>;
